/**
 * Foto-Schätzung im Hintergrund (28.10.2026, Timo: „Minuten warten ist
 * inakzeptabel“).
 *
 * Gemessen auf jillo-ci: Gemma 4 (12b) über Ollama schätzt so gut wie ein
 * großes Modell (kcal-Median-Abweichung 16 %), braucht aber **57–179 s je
 * Foto**. Deshalb wartet niemand mehr darauf: Das Gerät legt einen
 * **Auftrag** an (Kennung vom Gerät), der Server antwortet sofort, eine
 * Warteschlange im API-Prozess schätzt ein Foto nach dem anderen, und eine
 * Mitteilung sagt, wenn es fertig ist.
 *
 * Hier steht, was Server, Scheduler und Gerät gleich wissen müssen: Zustände,
 * Fristen, die Verpackung des Ergebnisses (Ende-zu-Ende) und wie ein noch
 * offener Platzhalter in Summen zählt — nämlich **nie als 0**.
 */

import type { DaySlot, Macros } from './nutrition.js';

/** Zustand beim Server. `done` heißt: Ergebnis liegt bereit, `failed` mit Grund. */
export const ESTIMATE_JOB_STATUSES = ['queued', 'running', 'done', 'failed'] as const;
export type EstimateJobStatus = (typeof ESTIMATE_JOB_STATUSES)[number];

/** Wie lange ein Auftrag beim Server liegt — danach räumt er ihn weg, abgeholt oder nicht. */
export const ESTIMATE_JOB_MAX_AGE_MS = 24 * 60 * 60 * 1000;

/** Mehr Aufträge in der Schlange nimmt der Server nicht an (`busy`) — eine Stunde Arbeit. */
export const ESTIMATE_QUEUE_MAX = 40;

/** Die Ansicht, die das Antippen der Mitteilung öffnet (`?ansicht=schaetzung`). */
export const ESTIMATE_VIEW_PARAM = 'schaetzung';

/** Ist dieser Zustand noch offen (der Server arbeitet daran)? */
export function isEstimateOpen(status: EstimateJobStatus): boolean {
  return status === 'queued' || status === 'running';
}

// --- Mitteilung -----------------------------------------------------------------

/**
 * Text der Mitteilung — **neutral**: weder Gericht noch Zahl. Die Werte sind
 * Ende-zu-Ende verschlüsselt, und auf dem Sperrbildschirm hätten sie ohnehin
 * nichts verloren.
 */
export function estimateNoticeText(
  outcome: 'done' | 'failed',
  locale: 'de' | 'en',
): { title: string; body: string } {
  if (locale === 'en') {
    return outcome === 'done'
      ? { title: 'Your nutrition values are ready', body: 'Take a quick look?' }
      : { title: 'The estimate did not work', body: 'Tap to see why — you can send it again.' };
  }
  return outcome === 'done'
    ? { title: 'Deine Nährwerte sind da', body: 'Kurz prüfen?' }
    : {
        title: 'Die Schätzung hat nicht geklappt',
        body: 'Antippen zeigt warum — du kannst sie erneut senden.',
      };
}

// --- Platzhalter in Summen ------------------------------------------------------

/**
 * Tagessumme mit offenen Schätzungen. Ein Platzhalter hat **keine** Werte —
 * er zählt nicht als 0, sondern als „offen“: Die Summe ist dann
 * „mindestens“, und die Anzeige sagt es.
 */
export interface PendingTotals {
  /** Summe der eingetragenen Werte, oder `null`, wenn nur Offenes da ist. */
  macros: Macros | null;
  /** Wie viele offene Schätzungen fehlen in der Summe. */
  open: number;
  /** Die Summe ist eine Untergrenze (es fehlt noch etwas). */
  atLeast: boolean;
}

export function totalsWithPending(entries: readonly Macros[], open: number): PendingTotals {
  if (entries.length === 0) return { macros: null, open, atLeast: false };
  const macros = entries.reduce<Macros>(
    (sum, entry) => ({
      kcal: sum.kcal + entry.kcal,
      protein: sum.protein + entry.protein,
      carbs: sum.carbs + entry.carbs,
      fat: sum.fat + entry.fat,
    }),
    { kcal: 0, protein: 0, carbs: 0, fat: 0 },
  );
  return { macros, open, atLeast: open > 0 };
}

/** Welche Plätze eines Tages offene Schätzungen tragen — für die Mahlzeiten-Erinnerung. */
export function slotsWithEstimates(
  jobs: readonly { date: string; slot: DaySlot; status: EstimateJobStatus }[],
  date: string,
): Set<DaySlot> {
  return new Set(
    jobs.filter((job) => job.date === date && job.status !== 'failed').map((job) => job.slot),
  );
}

// --- Ergebnis Ende-zu-Ende ------------------------------------------------------

/**
 * Das Ergebnis verlässt den Server nur verschlüsselt, wenn das Konto ein
 * Schlüsselpaar hat (`encryption_keys.kind = 'identity'`, P-256): ECDH mit
 * einem **Einmal-Schlüssel**, HKDF-SHA-256 (Salz = Auftragskennung, Info
 * `kraftwerk-e2e-estimate`), AES-GCM. Dieselbe Form wie die Verpackung des
 * Haushaltsschlüssels: Einmal-Schlüssel (65) ‖ IV (12) ‖ Chiffrat, base64url.
 * Öffnen kann nur ein Gerät mit dem privaten Schlüssel des Kontos.
 */
export const ESTIMATE_SEAL_INFO = 'kraftwerk-e2e-estimate';

const subtle = () => globalThis.crypto.subtle;
/** `CryptoKey` aus Node- wie Browser-Typen — `shared` kennt kein DOM. */
type Key = Awaited<ReturnType<typeof globalThis.crypto.subtle.importKey>>;

function toB64url(bytes: Uint8Array): string {
  let binary = '';
  for (const byte of bytes) binary += String.fromCharCode(byte);
  return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

function fromB64url(value: string): Uint8Array<ArrayBuffer> {
  const padded = value.replace(/-/g, '+').replace(/_/g, '/');
  const binary = atob(padded + '='.repeat((4 - (padded.length % 4)) % 4));
  return Uint8Array.from(binary, (char) => char.charCodeAt(0));
}

async function sealKey(
  privateKey: Key,
  publicRaw: Uint8Array<ArrayBuffer>,
  jobId: string,
): Promise<Key> {
  const peer = await subtle().importKey(
    'raw',
    publicRaw,
    { name: 'ECDH', namedCurve: 'P-256' },
    false,
    [],
  );
  const bits = await subtle().deriveBits({ name: 'ECDH', public: peer }, privateKey, 256);
  const base = await subtle().importKey('raw', bits, 'HKDF', false, ['deriveKey']);
  return subtle().deriveKey(
    {
      name: 'HKDF',
      hash: 'SHA-256',
      salt: new TextEncoder().encode(jobId),
      info: new TextEncoder().encode(ESTIMATE_SEAL_INFO),
    },
    base,
    { name: 'AES-GCM', length: 256 },
    false,
    ['encrypt', 'decrypt'],
  );
}

/** Für den öffentlichen Schlüssel des Kontos verschlüsseln (Server). */
export async function sealEstimateResult(
  value: unknown,
  publicKey: string,
  jobId: string,
): Promise<string> {
  const ephemeral = await subtle().generateKey({ name: 'ECDH', namedCurve: 'P-256' }, true, [
    'deriveBits',
  ]);
  const key = await sealKey(ephemeral.privateKey, fromB64url(publicKey), jobId);
  const iv = globalThis.crypto.getRandomValues(new Uint8Array(12));
  const plain = new TextEncoder().encode(JSON.stringify(value));
  const cipher = new Uint8Array(await subtle().encrypt({ name: 'AES-GCM', iv }, key, plain));
  const ephemeralRaw = new Uint8Array(await subtle().exportKey('raw', ephemeral.publicKey));
  const out = new Uint8Array(ephemeralRaw.length + iv.length + cipher.length);
  out.set(ephemeralRaw);
  out.set(iv, ephemeralRaw.length);
  out.set(cipher, ephemeralRaw.length + iv.length);
  return toB64url(out);
}

/** Mit dem privaten Schlüssel (PKCS#8) des Kontos öffnen (Gerät). Wirft, wenn er nicht passt. */
export async function openEstimateResult(
  sealed: string,
  privatePkcs8: Uint8Array<ArrayBuffer>,
  jobId: string,
): Promise<unknown> {
  const bytes = fromB64url(sealed);
  const privateKey = await subtle().importKey(
    'pkcs8',
    privatePkcs8,
    { name: 'ECDH', namedCurve: 'P-256' },
    false,
    ['deriveBits'],
  );
  const key = await sealKey(privateKey, bytes.slice(0, 65), jobId);
  const plain = await subtle().decrypt(
    { name: 'AES-GCM', iv: bytes.slice(65, 77) },
    key,
    bytes.slice(77),
  );
  return JSON.parse(new TextDecoder().decode(plain)) as unknown;
}
